import { motion } from 'framer-motion'
import { Database, Upload, FileText, Search, BookOpen } from 'lucide-react'

const KnowledgeBase = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="prose prose-invert max-w-none"
    >
      <h1 className="text-3xl font-bold mb-6 flex items-center">
        <Database className="mr-3 text-accent-cyan" size={32} />
        知识库管理
      </h1>

      {/* 知识库介绍 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">什么是知识库</h2>
        <p className="text-gray-400 mb-4">
          知识库是 Avelia 的长期记忆系统，你可以导入文档、笔记、资料等内容，AI 会基于这些知识来回答问题和执行任务。
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 not-prose">
          <div className="glass-effect rounded-lg p-5">
            <BookOpen className="text-accent-cyan mb-3" size={28} />
            <h3 className="font-semibold mb-2 text-lg">个人知识库</h3>
            <p className="text-sm text-gray-400">
              导入你的文档、笔记、项目资料，构建专属知识体系。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-5">
            <Search className="text-accent-cyan mb-3" size={28} />
            <h3 className="font-semibold mb-2 text-lg">智能检索</h3>
            <p className="text-sm text-gray-400">
              在提问时明确提及使用知识库，AI 会检索相关内容并提供精准答案。
            </p>
          </div>
          
          <div className="glass-effect rounded-lg p-5">
            <FileText className="text-accent-cyan mb-3" size={28} />
            <h3 className="font-semibold mb-2 text-lg">多格式支持</h3>
            <p className="text-sm text-gray-400">
              支持 PDF、Word、TXT、Markdown 等多种文档格式。
            </p>
          </div>
        </div>
      </section>

      {/* 添加知识 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">添加知识</h2>
        
        <div className="space-y-4">
          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Upload className="mr-2 text-blue-400" size={20} />
              方式一：上传文件
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-xs font-bold text-blue-300">
                  1
                </div>
                <p className="text-gray-400">
                  打开设置窗口，进入"知识库"页面
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-xs font-bold text-blue-300">
                  2
                </div>
                <p className="text-gray-400">
                  点击"上传文件"按钮，选择要导入的文档
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-xs font-bold text-blue-300">
                  3
                </div>
                <p className="text-gray-400">
                  系统会自动提取文档内容并建立索引
                </p>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-blue-500/10 rounded border border-blue-500/30 text-xs">
              <p className="text-blue-300 mb-2"><strong>支持的文件格式：</strong></p>
              <ul className="text-gray-400 space-y-1 ml-4">
                <li>• <strong>文档：</strong>PDF, DOCX, DOC, TXT, MD</li>
                <li>• <strong>数据：</strong>JSON, XML, CSV</li>
                <li>• <strong>网页：</strong>HTML, HTM</li>
              </ul>
            </div>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <FileText className="mr-2 text-purple-400" size={20} />
              方式二：手动输入
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center text-xs font-bold text-purple-300">
                  1
                </div>
                <p className="text-gray-400">
                  在知识库页面点击"添加文本"
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center text-xs font-bold text-purple-300">
                  2
                </div>
                <p className="text-gray-400">
                  输入标题和内容（支持 Markdown 格式）
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center text-xs font-bold text-purple-300">
                  3
                </div>
                <p className="text-gray-400">
                  点击"添加"保存到知识库
                </p>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-purple-500/10 rounded border border-purple-500/30 text-xs">
              <p className="text-purple-300 mb-2"><strong>适用场景：</strong></p>
              <ul className="text-gray-400 space-y-1 ml-4">
                <li>• 快速记录重要信息</li>
                <li>• 添加个人笔记和心得</li>
                <li>• 补充文档中缺失的内容</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 使用知识库 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">使用知识库</h2>
        
        <div className="space-y-4">
          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">🔍 主动查询</h3>
            <p className="text-sm text-gray-400 mb-3">
              要使用知识库，需要在提问时明确提及查询知识库或相关文档：
            </p>
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-green-500/10 rounded border-l-4 border-green-500">
                <strong className="text-green-300">示例 1：</strong>
                <p className="text-gray-400 mt-1">
                  <strong>你问：</strong>"查询知识库，我们项目的技术栈是什么？"<br/>
                  <strong>AI 答：</strong>基于你导入的项目文档，回答技术栈信息
                </p>
              </div>
              <div className="p-3 bg-green-500/10 rounded border-l-4 border-green-500">
                <strong className="text-green-300">示例 2：</strong>
                <p className="text-gray-400 mt-1">
                  <strong>你问：</strong>"根据会议纪要，上次讨论的重点是什么？"<br/>
                  <strong>AI 答：</strong>从会议纪要中提取关键信息
                </p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-blue-500/10 rounded border border-blue-500/30 text-xs">
              <p className="text-blue-300 mb-2"><strong>💡 触发关键词：</strong></p>
              <ul className="text-gray-400 space-y-1 ml-4">
                <li>• "查询知识库"、"搜索文档"</li>
                <li>• "根据文档"、"参考资料"</li>
                <li>• "在知识库中查找"</li>
                <li>• 或直接提及具体文档名称</li>
              </ul>
            </div>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">📚 知识引用</h3>
            <p className="text-sm text-gray-400 mb-3">
              AI 回答时会标注信息来源，你可以追溯到原始文档：
            </p>
            <ul className="text-sm text-gray-400 space-y-2 ml-4">
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>回答中会显示"来源：XXX文档"</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>可以在知识库页面查看完整文档</span>
              </li>
              <li className="flex items-start">
                <span className="text-accent-cyan mr-2">•</span>
                <span>确保信息的准确性和可追溯性</span>
              </li>
            </ul>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3">🎯 精准提问</h3>
            <p className="text-sm text-gray-400 mb-3">
              提高知识库检索准确度的技巧：
            </p>
            <div className="space-y-2 text-sm">
              <div className="p-3 bg-blue-500/10 rounded">
                <strong className="text-blue-300">明确提及文档：</strong>
                <p className="text-gray-400 mt-1">
                  "根据项目文档，API 接口的认证方式是什么？"
                </p>
              </div>
              <div className="p-3 bg-blue-500/10 rounded">
                <strong className="text-blue-300">指定具体文档：</strong>
                <p className="text-gray-400 mt-1">
                  "在《产品需求文档》中，用户注册流程是怎样的？"
                </p>
              </div>
              <div className="p-3 bg-blue-500/10 rounded">
                <strong className="text-blue-300">使用查询关键词：</strong>
                <p className="text-gray-400 mt-1">
                  "查询知识库，总结我所有关于 Python 的学习笔记"
                </p>
              </div>
            </div>
            <div className="mt-3 p-3 bg-yellow-500/10 rounded border border-yellow-500/30 text-xs">
              <p className="text-yellow-200">
                ⚠️ <strong>重要：</strong>如果不在提问中明确提及使用知识库或文档，AI 将基于通用知识回答，不会检索你的知识库内容。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 管理知识库 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">管理知识库</h2>
        
        <div className="glass-effect rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-3 font-semibold">操作</th>
                <th className="text-left p-3 font-semibold">说明</th>
              </tr>
            </thead>
            <tbody className="text-gray-400">
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">查看详情</td>
                <td className="p-3">点击知识条目标题，查看完整内容和元信息</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">搜索知识</td>
                <td className="p-3">使用搜索框快速定位特定内容</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">删除知识</td>
                <td className="p-3">点击删除按钮移除不需要的内容</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-semibold text-white">批量管理</td>
                <td className="p-3">可以批量删除或导出知识条目</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 最佳实践 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">最佳实践</h2>
        
        <div className="space-y-3">
          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 组织清晰</h4>
            <p className="text-sm text-gray-400">
              给文档起有意义的标题，方便后续查找和引用。例如："2024年Q1产品规划"而不是"文档1"。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 及时更新</h4>
            <p className="text-sm text-gray-400">
              当文档内容发生变化时，重新上传最新版本，删除过时的内容。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-green-500">
            <h4 className="font-semibold mb-2 text-green-300">✅ 内容质量</h4>
            <p className="text-sm text-gray-400">
              导入结构清晰、内容准确的文档，避免格式混乱或信息错误的资料。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-semibold mb-2 text-yellow-300">⚠️ 隐私保护</h4>
            <p className="text-sm text-gray-400">
              知识库内容存储在本地，但请注意不要导入包含敏感信息（如密码、密钥）的文档。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-semibold mb-2 text-yellow-300">⚠️ 存储空间</h4>
            <p className="text-sm text-gray-400">
              大量文档会占用存储空间，定期清理不需要的内容，保持知识库精简高效。
            </p>
          </div>
        </div>
      </section>

      {/* 使用场景 */}
      <section className="mt-8 p-6 glass-effect rounded-lg border border-accent-cyan/30">
        <h3 className="text-xl font-semibold mb-4">💡 典型使用场景</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="p-3 bg-blue-500/10 rounded">
            <strong className="text-blue-300">项目管理：</strong>
            <p className="text-gray-400 mt-1">导入项目文档、需求说明、技术方案，快速查询项目信息</p>
          </div>
          <div className="p-3 bg-blue-500/10 rounded">
            <strong className="text-blue-300">学习笔记：</strong>
            <p className="text-gray-400 mt-1">整理学习资料、课程笔记，构建个人知识体系</p>
          </div>
          <div className="p-3 bg-blue-500/10 rounded">
            <strong className="text-blue-300">工作资料：</strong>
            <p className="text-gray-400 mt-1">保存工作流程、操作手册、常见问题解答</p>
          </div>
          <div className="p-3 bg-blue-500/10 rounded">
            <strong className="text-blue-300">个人档案：</strong>
            <p className="text-gray-400 mt-1">记录重要信息、联系人、日程安排等</p>
          </div>
        </div>
      </section>
    </motion.div>
  )
}

export default KnowledgeBase
